import type { IMeResponse } from "../types/auth";
import type {
  ShiftReportPeriodEnum,
  ShiftTimesheetAllResponse,
  ShiftTimesheetResponse,
} from "../types/employee-shift-report";
import type {
  EvaluatedFeatureFlagsResponse,
  FeatureFlagPagedResponse,
  FeatureFlagQueryDto,
  FeatureFlagResponse,
} from "../types/feature-flag";
import type {
  PagedPayrollResponse,
  QueryPayrollDto,
} from "../types/payroll";
import type {
  EmployementRequestQuery,
  EmployementResponse,
  EmployementResponseById,
  EmploymentManagerIdCheckResponse,
  EmploymentsWithNoActiveEmploymentResponse,
} from "../types/employement";
import type {
  ActivityQueryDto,
  PagedActivityResponse,
} from "../types/activity";
import type { Leave, LeaveRequestQuery, LeaveResponse } from "../types/leave";
import type {
  LeaveTypeRequestQuery,
  LeaveTypeResponse,
  SingleLeaveTypeResponse,
} from "../types/leave-type";
import type {
  IUserByIDResponse,
  IUserQuery,
  IUserResponse,
} from "../types/user";
import type {
  PagedDocumentsResponse,
  DocumentDownloadResponse,
} from "../types/documents";
import type { IQueryPaginationDto } from "../types/utils";
import { buildQueryParams } from "../utils/utils";
import type {
  DailyShiftsFilters,
  DailyShiftsResponse,
} from "../types/daily-shifts";
import type {
  IOvertimeItemWrapper,
  IOvertimeListResponse,
  IOvertimeQueryRequest,
} from "../types/overtime";
import type {
  PagedUnitResponse,
  UnitResponse,
  UnitQueryParams,
} from "../types/unit";
import type {
  LeaveApprovalProcess,
  LeaveApprovalProcessResponse,
  LeaveApprovalProcessQueryParams,
} from "../types/leave-approval-process";
import type {
  OvertimeApprovalProcess,
  OvertimeApprovalProcessResponse,
  OvertimeApprovalProcessQueryParams,
} from "../types/overtime-approval-process";
import type {
  CalendarHolidayResponse,
  CalendarHolidaysPagedResponse,
  CalendarHolidaysQueryDto,
} from "../types/calendar-holidays";
import type { AppVersionResponse, AppPlatform } from "../types/app-version";
import type {
  AnnouncementRequestQuery,
  PagedAnnouncementResponse,
} from "../types/announcement";
import type {
  gatePagedResponse,
  gateResponse,
  gatesQueryDto,
} from "../types/gate";
import type {
  factoryPagedResponse,
  factoryResponse,
  factoriesQueryDto,
} from "../types/factory";
import type {
  IGuestByIDResponse,
  IGuestQuery,
  IGuestResponse,
} from "../types/guest";
import type {
  IEntranceByIDResponse,
  IEntranceQuery,
  IEntranceResponse,
} from "../types/entrance";
import type {
  RealmRequestQuery,
  PagedRealmResponse,
  Realm,
} from "../types/realm";
import type {
  AnnualLeaveBalance,
  AnnualLeaveBalanceAsOfQuery,
  AnnualLeaveBalanceAsOfResponse,
  AnnualLeaveBalanceResponse,
  AnnualLeaveYearlyQuery,
  AnnualLeaveYearlyResponse,
} from "../types/annual-leave-balance";
import { http } from "./http";
import type {
  GuestEntrance,
  GuestEntranceReportsQuery,
} from "../types/guest-entrance-reports";
import type {
  EmploymentStartReportResponse,
  EmploymentStartReportsQuery,
} from "../types/employment-start-reports";
import type {
  UserLeaveReportQuery,
  UserLeaveReportResponse,
} from "../types/user-leave-reports";
import type { Device } from "../types/device";
import type {
  PagedWorkCalendarResponse,
  SingleWorkCalendarResponse,
  WorkCalendarRequestQuery,
} from "../types/work-calendar";
import type { CountriesResponse, CountryQueryParams } from "../types/country";
import type {
  ILocationHeatmapQuery,
  ILocationHeatmapResponse,
} from "../types/location";
import type { NetgsmBalanceResponse } from "../types/sms";

export const getMe = (token: string): Promise<IMeResponse> =>
  http(`v1/auth/me`, { token });

export const netgsmBalance = (token: string): Promise<NetgsmBalanceResponse> =>
  http(`v1/sms/netgsm/balance`, { token });

export const gates = (
  token: string,
  query?: gatesQueryDto,
): Promise<gatePagedResponse> =>
  http(`v1/gate`, { token, query: buildQueryParams(query) });

export const factories = (
  token: string,
  query?: factoriesQueryDto,
): Promise<factoryPagedResponse> =>
  http(`v1/factory`, { token, query: buildQueryParams(query) });

export const factoryById = (
  token: string,
  factoryId: string | undefined,
): Promise<factoryResponse> => http(`v1/factory/${factoryId}`, { token });

export const workCalendars = (
  token: string,
  query?: WorkCalendarRequestQuery,
): Promise<PagedWorkCalendarResponse> =>
  http(`v1/work-calendar`, { token, query: buildQueryParams(query) });

export const calendarHolidays = (
  token: string,
  query?: CalendarHolidaysQueryDto,
): Promise<CalendarHolidaysPagedResponse> =>
  http(`v1/calendar-holidays`, { token, query: buildQueryParams(query) });

export const calendarHolidayById = (
  token: string,
  holidayId: string | undefined,
): Promise<CalendarHolidayResponse> =>
  http(`v1/calendar-holidays/${holidayId}`, { token });

export const gateById = (
  token: string,
  gateId: string | undefined,
): Promise<gateResponse> => http(`v1/gate/${gateId}`, { token });

export const workCalendarById = (
  token: string,
  workCalendarId: string | undefined,
): Promise<SingleWorkCalendarResponse> =>
  http(`v1/work-calendar/${workCalendarId}`, { token });

export const employements = (
  token: string,
  employementQuery?: EmployementRequestQuery,
): Promise<EmployementResponse> =>
  http(`v1/employment`, { token, query: buildQueryParams(employementQuery) });

export const getEmploymentById = (
  token: string,
  employmentId: string,
): Promise<EmployementResponseById> =>
  http(`v1/employment/${employmentId}`, { token });

export const employmentManagerCheck = (
  token: string,
  employmentId: string,
): Promise<EmploymentManagerIdCheckResponse> =>
  http(`v1/employment/employmentManagerIdCheck/${employmentId}`, { token });

export const guestById = (
  token: string,
  guestId: string,
): Promise<IGuestByIDResponse> => http(`v1/guest/id/${guestId}`, { token });

export const activity = (
  token: string,
  activityQuery?: ActivityQueryDto,
): Promise<PagedActivityResponse> =>
  http(`v1/activities`, { token, query: buildQueryParams(activityQuery) });

export const deviceById = (token: string, deviceId: string): Promise<Device> =>
  http(`v1/devices/${deviceId}`, { token });

export const realms = (
  token: string,
  realmQuery?: RealmRequestQuery,
): Promise<PagedRealmResponse> =>
  http(`v1/realm`, { token, query: buildQueryParams(realmQuery) });

export const realmById = (token: string, realmId: string): Promise<Realm> =>
  http(`v1/realm/${realmId}`, { token });

export const guests = (
  token: string,
  guestQuery?: IGuestQuery,
): Promise<IGuestResponse> =>
  http(`v1/guest`, { token, query: buildQueryParams(guestQuery) });

export const entrance = (
  token: string,
  entranceQuery?: IEntranceQuery,
): Promise<IEntranceResponse> =>
  http(`v1/entrance`, { token, query: buildQueryParams(entranceQuery) });

export const entranceById = (
  token: string,
  entranceId: string,
): Promise<IEntranceByIDResponse> =>
  http(`v1/entrance/${entranceId}`, { token });

export const users = (
  token: string,
  userQuery?: IUserQuery,
): Promise<IUserResponse> =>
  http(`v1/user`, { token, query: buildQueryParams(userQuery) });

export const userById = (
  token: string,
  userId: string,
): Promise<IUserByIDResponse> => http(`v1/user/${userId}`, { token });

export const leaves = (
  token: string,
  leaveQuery?: LeaveRequestQuery,
): Promise<LeaveResponse> =>
  http(`v1/leave`, { token, query: buildQueryParams(leaveQuery) });

export const leaveById = (
  token: string,
  leaveId: string,
): Promise<{ result: Leave }> => http(`v1/leave/${leaveId}`, { token });

export const leaveTypes = (
  token: string,
  leaveTypeQuery?: LeaveTypeRequestQuery,
): Promise<LeaveTypeResponse> =>
  http(`v1/leave-type`, { token, query: buildQueryParams(leaveTypeQuery) });

export const leaveTypeById = (
  token: string,
  leaveTypeId: string,
): Promise<SingleLeaveTypeResponse> =>
  http(`v1/leave-type/${leaveTypeId}`, { token });

export const userDocuments = (
  token: string,
  userId: string,
  query?: IQueryPaginationDto,
): Promise<PagedDocumentsResponse> =>
  http(`v1/user/${userId}/documents`, {
    token,
    query: buildQueryParams(query),
  });

export const getDocumentDownloadUrl = (
  token: string,
  userId: string,
  documentId: string,
): Promise<DocumentDownloadResponse> =>
  http(`v1/user/${userId}/documents/${documentId}/download`, { token });

export const getOvertime = (
  token: string,
  filters?: IOvertimeQueryRequest,
): Promise<IOvertimeListResponse> =>
  http(`v1/overtime`, { token, query: buildQueryParams(filters) });

export const overtimeById = (
  token: string,
  overtimeId: string,
): Promise<IOvertimeItemWrapper> =>
  http(`v1/overtime/${overtimeId}`, { token });

export const units = (
  token: string,
  unitQuery?: UnitQueryParams,
): Promise<PagedUnitResponse> =>
  http(`v1/unit`, { token, query: buildQueryParams(unitQuery) });

export const payrolls = (
  token: string,
  filters?: QueryPayrollDto,
): Promise<PagedPayrollResponse> =>
  http("v1/payroll", { token, query: buildQueryParams(filters) });

export const getUnitById = (
  token: string,
  unitId: string | undefined,
): Promise<UnitResponse> => http(`v1/unit/${unitId}`, { token });

export const leaveApprovalProcesses = (
  token: string,
  queryParams?: LeaveApprovalProcessQueryParams,
): Promise<LeaveApprovalProcessResponse> =>
  http(`v1/leave-approval-process`, {
    token,
    query: buildQueryParams(queryParams),
  });

export const leaveApprovalProcessById = (
  token: string,
  processId: string | undefined,
): Promise<{ result: LeaveApprovalProcess }> =>
  http(`v1/leave-approval-process/${processId}`, { token });

export const overtimeApprovalProcesses = (
  token: string,
  queryParams?: OvertimeApprovalProcessQueryParams,
): Promise<OvertimeApprovalProcessResponse> =>
  http(`v1/overtime-approval-process`, {
    token,
    query: buildQueryParams(queryParams),
  });

export const overtimeApprovalProcessById = (
  token: string,
  processId: string | undefined,
): Promise<{ result: OvertimeApprovalProcess }> =>
  http(`v1/overtime-approval-process/${processId}`, { token });

export const announcements = (
  token: string,
  query?: AnnouncementRequestQuery,
): Promise<PagedAnnouncementResponse> =>
  http(`v1/announcement`, { token, query: buildQueryParams(query) });

export const employmentManagerIdCheckByUserId = (
  token: string,
  id: string | undefined,
): Promise<EmploymentManagerIdCheckResponse> =>
  http(`v1/employment/employmentManagerIdCheck/${id}`, { token });

export const getLeave = (
  token: string,
  query?: AnnualLeaveBalance,
): Promise<AnnualLeaveBalanceResponse> =>
  http(`v1/reports/annual-leave-balances`, {
    token,
    query: buildQueryParams(query),
  });

export const getAnnualLeaveBalancesAsOf = (
  token: string,
  query: AnnualLeaveBalanceAsOfQuery,
): Promise<AnnualLeaveBalanceAsOfResponse> =>
  http(`v1/reports/annual-leave-balances/as-of`, {
    token,
    query: buildQueryParams(query),
  });

export const getAnnualLeaveYearly = (
  token: string,
  query: AnnualLeaveYearlyQuery,
): Promise<AnnualLeaveYearlyResponse> =>
  http(`v1/reports/annual-leave-balances/yearly`, {
    token,
    query: buildQueryParams(query),
  });

export const managersWithNoActiveEmployment = (
  token: string,
): Promise<EmploymentsWithNoActiveEmploymentResponse> =>
  http(`v1/employment/ManagersWithNoActiveEmployments`, { token });
export const getGuestEntranceReports = (
  token: string,
  query?: GuestEntranceReportsQuery,
): Promise<GuestEntrance[]> =>
  http(`v1/reports/guest-entrance-report`, {
    token,
    query: buildQueryParams(query),
  });

export const getEmploymentStartReports = (
  token: string,
  query?: EmploymentStartReportsQuery,
): Promise<EmploymentStartReportResponse[]> =>
  http(`v1/reports/employment-start-report`, {
    token,
    query: buildQueryParams(query),
  });

export const getUserLeaveReports = (
  token: string,
  query?: UserLeaveReportQuery,
): Promise<UserLeaveReportResponse[]> =>
  http(`v1/reports/user-leave-report`, {
    token,
    query: buildQueryParams(query),
  });

export const getShiftTimesheet = (
  token: string,
  query: {
    userId: string;
    period: ShiftReportPeriodEnum;
    date?: string;
  },
): Promise<ShiftTimesheetResponse> =>
  http(`v1/reports/shift-timesheet`, {
    token,
    query: buildQueryParams(query),
  });

export const getShiftTimesheetAll = (
  token: string,
  query: {
    period: ShiftReportPeriodEnum;
    date?: string;
    unitId?: string;
  },
): Promise<ShiftTimesheetAllResponse> =>
  http(`v1/reports/shift-timesheet/all`, {
    token,
    query: buildQueryParams(query),
  });

export const getDailyShifts = (
  token: string,
  query: DailyShiftsFilters,
): Promise<DailyShiftsResponse> =>
  http(`v1/reports/daily-shifts`, {
    token,
    query: buildQueryParams(query),
  });

// App Version API Functions
export const getAppVersion = (
  token: string,
  platform: AppPlatform,
): Promise<AppVersionResponse> => http(`v1/app-version/${platform}`, { token });

export const country = (
  token: string,
  countryQuery?: CountryQueryParams,
): Promise<CountriesResponse> =>
  http(`v1/country`, { token, query: buildQueryParams(countryQuery) });

// Location API Functions
export const locationHeatmap = (
  token: string,
  query: ILocationHeatmapQuery,
): Promise<ILocationHeatmapResponse> =>
  http(`v1/locations/heatmap`, { token, query: buildQueryParams(query) });

export const featureFlags = (
  token: string,
  query?: FeatureFlagQueryDto,
): Promise<FeatureFlagPagedResponse> =>
  http(`v1/feature-flag`, { token, query: buildQueryParams(query) });

export const featureFlagById = (
  token: string,
  flagId: string | undefined,
): Promise<FeatureFlagResponse> => http(`v1/feature-flag/${flagId}`, { token });

export const evaluateFeatureFlags = (
  token: string,
): Promise<EvaluatedFeatureFlagsResponse> =>
  http(`v1/feature-flag/evaluate/me`, { token });
